import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchMyLinks, fetchAllLinks, type Link as LinkT, type MeResponse } from '../lib/api';

export default function Home({ me }: { me: MeResponse | null }) {
  const isAdmin = !!me?.is_admin;
  const [showAll, setShowAll] = useState(false);
  const [links, setLinks] = useState<LinkT[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    setError(null);
    const fn = isAdmin && showAll ? fetchAllLinks : fetchMyLinks;
    fn()
      .then((r) => setLinks(r.data ?? []))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [isAdmin, showAll]);

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-semibold">{showAll ? 'All links (admin)' : 'Your links'}</h1>
        <div className="flex items-center gap-4">
          {isAdmin && (
            <label className="flex items-center gap-2 text-sm text-gray-600">
              <input
                type="checkbox"
                checked={showAll}
                onChange={(e) => setShowAll(e.target.checked)}
                className="rounded"
              />
              Show all users' links
            </label>
          )}
          <Link
            to="/new"
            className="bg-blue-600 text-white text-sm rounded-lg px-3 py-1.5 hover:bg-blue-700"
          >
            + New link
          </Link>
        </div>
      </div>

      {loading && <p className="text-gray-500">Loading…</p>}
      {error && <p className="text-red-600">{error}</p>}

      {!loading && !error && links.length === 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-gray-500">
          No links yet.
        </div>
      )}

      {!loading && links.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200 text-gray-600 text-xs uppercase">
              <tr>
                <th className="px-4 py-3 text-left font-medium">Slug</th>
                <th className="px-4 py-3 text-left font-medium">Type</th>
                <th className="px-4 py-3 text-left font-medium">Destination</th>
                {showAll && <th className="px-4 py-3 text-left font-medium">Owner</th>}
                <th className="px-4 py-3 text-left font-medium">Active</th>
                <th className="px-4 py-3 text-left font-medium">Scans</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {links.map((l) => (
                <tr key={l.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <Link to={`/links/${l.id}`} className="font-mono text-blue-600 hover:underline">
                      {l.id}
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-gray-700">{l.type}</td>
                  <td className="px-4 py-3 text-gray-700 truncate max-w-xs">{l.destination}</td>
                  {showAll && <td className="px-4 py-3 text-gray-500 font-mono text-xs">{l.user_id}</td>}
                  <td className="px-4 py-3">
                    {l.active ? (
                      <span className="text-green-700 bg-green-100 px-2 py-0.5 rounded text-xs">live</span>
                    ) : (
                      <span className="text-gray-500 bg-gray-100 px-2 py-0.5 rounded text-xs">draft</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-700">{l.scanCount ?? 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
